import { X } from 'lucide-react';
import {
  useEffect,
  useId,
  useRef,
  useState,
  type FormEvent,
  type KeyboardEvent as ReactKeyboardEvent,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Button, ErrorBox } from './ui.tsx';

// Janela sobreposta (modal) do guia visual: cadastro rápido, edição, anexo de documento e
// confirmação abrem por cima da tela em que a pessoa está, sem trocar de página.
// Regras de uso em docs/guia-visual/README.md, seção "Modal". Base do PR #105, com o tamanho da
// ficha com abas (`record`), a faixa de abas fixa e a densidade compacta dos Dados Mestres.

export type ModalSize = 'sm' | 'md' | 'lg' | 'xl' | 'record';

export const MODAL_WIDTHS: Record<ModalSize, string> = {
  sm: 'sm:max-w-sm', // 384px: confirmação, uma pergunta
  md: 'sm:max-w-lg', // 512px: cadastro curto (até ~6 campos), anexo de documento
  lg: 'sm:max-w-2xl', // 672px: cadastro em duas colunas
  xl: 'sm:max-w-4xl', // 896px: cadastro com itens (linhas de pedido) ou escolha em lista
  record: 'sm:max-w-5xl sm:h-[84vh]', // 1024px, altura fixa: ficha com abas (ex.: Empresa)
};

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/** Próximo índice de foco ao apertar Tab dentro da janela: dá a volta nas pontas. */
export function nextFocusIndex(current: number, total: number, backwards: boolean): number {
  if (total === 0) return -1;
  if (current < 0) return backwards ? total - 1 : 0;
  return (current + (backwards ? -1 : 1) + total) % total;
}

type ModalProps = {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  description?: ReactNode;
  size?: ModalSize;
  /** Há algo preenchido e não salvo: fechar pede confirmação antes de descartar. */
  dirty?: boolean;
  /** Com onSubmit, o conteúdo vira um formulário: Enter salva e o botão type="submit" do rodapé envia. */
  onSubmit?: (event: FormEvent<HTMLFormElement>) => void;
  /** Botões do rodapé, já na ordem: Cancelar (secundário) e depois a ação principal. */
  footer?: ReactNode;
  /** Faixa fixa logo abaixo do cabeçalho (as abas da ficha): não rola com o corpo. */
  tabs?: ReactNode;
  /** Campos de 36px no computador (no celular continuam com a altura normal). */
  compact?: boolean;
  /** Substitui o rodapé inteiro (ex.: a faixa de confirmação de quem chama). */
  footerBar?: ReactNode;
  /** Texto da confirmação de descarte, quando o padrão não serve. */
  discardText?: ReactNode;
  children?: ReactNode;
};

/** Faixa "Descartar as alterações?" do rodapé: a mesma no fechar da janela e no cancelar de uma ficha. */
export function DiscardBar({
  text,
  onKeep,
  onDiscard,
}: {
  text?: ReactNode;
  onKeep: () => void;
  onDiscard: () => void;
}) {
  const { t } = useTranslation();
  return (
    <footer
      role="alert"
      className="flex flex-wrap items-center justify-end gap-2 border-t border-rule bg-danger/5 px-5 py-3"
    >
      <span className="mr-auto text-sm">
        <strong className="font-semibold">{t('modal.discardTitle')}</strong>{' '}
        {text ?? t('modal.discardText')}
      </span>
      <Button type="button" variant="secondary" onClick={onKeep}>
        {t('modal.keepEditing')}
      </Button>
      <Button type="button" variant="danger" onClick={onDiscard}>
        {t('modal.discard')}
      </Button>
    </footer>
  );
}

export function Modal({ open, ...props }: ModalProps) {
  if (!open || typeof document === 'undefined') return null;
  return createPortal(<ModalFrame {...props} />, document.body);
}

export function ModalFrame({
  onClose,
  title,
  description,
  size = 'md',
  dirty = false,
  onSubmit,
  footer,
  tabs,
  compact = false,
  footerBar,
  discardText,
  children,
}: Omit<ModalProps, 'open'>) {
  const { t } = useTranslation();
  const titleId = useId();
  const descriptionId = useId();
  const dialogRef = useRef<HTMLDivElement>(null);
  const [confirmingDiscard, setConfirmingDiscard] = useState(false);

  const requestClose = () => (dirty ? setConfirmingDiscard(true) : onClose());

  // Foco entra no primeiro campo, a página de trás não rola e, ao fechar, o foco volta
  // para o botão que abriu a janela.
  useEffect(() => {
    const opener = document.activeElement as HTMLElement | null;
    const dialog = dialogRef.current;
    const first =
      dialog?.querySelector<HTMLElement>('[data-autofocus]') ??
      dialog?.querySelector<HTMLElement>(
        'input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled])',
      ) ??
      dialog;
    first?.focus();
    const { overflow } = document.body.style;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = overflow;
      opener?.focus?.();
    };
  }, []);

  const onKeyDown = (e: ReactKeyboardEvent) => {
    if (e.key === 'Escape') {
      e.stopPropagation();
      if (confirmingDiscard) setConfirmingDiscard(false);
      else requestClose();
      return;
    }
    if (e.key !== 'Tab' || !dialogRef.current) return;
    const items = Array.from(dialogRef.current.querySelectorAll<HTMLElement>(FOCUSABLE));
    const next = nextFocusIndex(
      items.indexOf(document.activeElement as HTMLElement),
      items.length,
      e.shiftKey,
    );
    if (next >= 0) {
      e.preventDefault();
      items[next]!.focus();
    }
  };

  const content = (
    <>
      {children && (
        <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto px-5 py-4">
          {children}
        </div>
      )}
      {confirmingDiscard ? (
        <DiscardBar
          text={discardText}
          onKeep={() => setConfirmingDiscard(false)}
          onDiscard={onClose}
        />
      ) : footerBar ? (
        footerBar
      ) : (
        footer && (
          <footer
            className={`flex flex-wrap justify-end gap-2 px-5 py-3 ${children ? 'border-t border-rule' : ''}`}
          >
            {footer}
          </footer>
        )
      )}
    </>
  );

  return (
    <div
      className="fixed inset-0 z-50 flex items-stretch justify-center bg-ink/20 backdrop-blur-[2px] sm:items-start sm:overflow-y-auto sm:px-4 sm:py-[8vh]"
      onMouseDown={(e) => e.target === e.currentTarget && requestClose()}
      onKeyDown={onKeyDown}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={description ? descriptionId : undefined}
        tabIndex={-1}
        className={`flex w-full flex-col bg-sheet focus:outline-none sm:max-h-[84vh] sm:rounded-lg sm:border sm:border-rule sm:shadow-[0_12px_32px_rgb(23_32_43/0.16)] ${MODAL_WIDTHS[size]} ${compact ? 'ui-compact' : ''}`}
      >
        <header
          className={`flex items-start gap-3 px-5 ${tabs ? 'pt-4 pb-1' : 'border-b border-rule py-4'}`}
        >
          <div className="flex min-w-0 flex-1 flex-col gap-1">
            <h2 id={titleId} className="text-lg font-semibold">
              {title}
            </h2>
            {description && (
              <p id={descriptionId} className="text-sm text-muted">
                {description}
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={requestClose}
            aria-label={t('common.close')}
            className="-mr-2 rounded-md p-2 text-muted hover:bg-paper hover:text-ink"
          >
            <X size={18} aria-hidden />
          </button>
        </header>
        {tabs && <div className="shrink-0 border-b border-rule px-3">{tabs}</div>}
        {onSubmit ? (
          <form
            className="flex min-h-0 flex-1 flex-col"
            onSubmit={(e) => {
              e.preventDefault();
              onSubmit(e);
            }}
          >
            {content}
          </form>
        ) : (
          <div className="flex min-h-0 flex-1 flex-col">{content}</div>
        )}
      </div>
    </div>
  );
}

/** Confirmação curta: "Remover este documento?". Use tone="danger" para o que não se desfaz. */
export function ConfirmDialog({
  open,
  title,
  description,
  confirmLabel,
  cancelLabel,
  tone = 'primary',
  busy = false,
  error,
  onConfirm,
  onCancel,
}: {
  open: boolean;
  title: ReactNode;
  description?: ReactNode;
  confirmLabel: string;
  /** Rótulo do botão de voltar; útil quando a ação confirmada já se chama "Cancelar...". */
  cancelLabel?: string;
  tone?: 'primary' | 'danger';
  busy?: boolean;
  /** Erro da ação confirmada: a janela continua aberta e mostra o aviso. */
  error?: unknown;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  const { t } = useTranslation();
  return (
    <Modal
      open={open}
      onClose={onCancel}
      title={title}
      description={description}
      size="sm"
      footer={
        <>
          <Button type="button" variant="secondary" onClick={onCancel} data-autofocus>
            {cancelLabel ?? t('common.cancel')}
          </Button>
          <Button type="button" variant={tone} onClick={onConfirm} disabled={busy}>
            {confirmLabel}
          </Button>
        </>
      }
    >
      {error ? <ErrorBox error={error} /> : null}
    </Modal>
  );
}
